import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from 'view/components/common/Modal';
import QuestionResponse from 'models/QuestionResponse';
import { AppPage } from 'view/components/common/AppPage';
import useApiCall from 'contexts/ApiCall';
import StudentGradebook from 'models/StudentGradebook';
import CachingEngine from 'utils/CachingEngine';
import AssignmentSettings from 'view/components/student/assignment/AssignmentSettings';
import Question from 'models/Question';
import { QuestionType } from 'utils/types';
import EvaluationSubmission from 'models/EvaluationSubmission';
import evaluationService from 'services/evaluationService';
import StudentQuestion from 'models/StudentQuestion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import EvaluationFinishedPopup from 'view/components/student/assignment/evaluation/EvaluationFinishedPopup';
import MultipartAssignment from 'view/components/student/assignment/multipart/Multipart';

import 'view/style/student/components/evaluation/evaluation.css';

const MAX_ROUNDS = 5; // TODO: change this to 5

const StudentEvaluationPage: React.FC = () => {
  const [submission, setSubmission] = useState<EvaluationSubmission>();
  const { classroomId, submissionId } = useParams();
  const [questions, setQuestions] = useState<StudentQuestion[]>([]);
  const [viewSettings, setViewSettings] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  useEffect(() => {
    if (!classroomId || !submissionId || submission) return;
    CachingEngine.getData(`${classroomId}-gradebook`).then(
      (cachedGradebook) => {
        if (cachedGradebook) {
          // get the submission from the gradebook
          const gradebook =
            StudentGradebook.fromServerStudentGradebook(cachedGradebook);

          const gradebookSubmission = gradebook.evaluations.find(
            (e) => e.id === submissionId,
          );

          if (gradebookSubmission) {
            setSubmission(gradebookSubmission);
            return;
          }
        }

        makeApiCall(evaluationService.getSubmissionById, submissionId)
          .then((respSubmission) =>
            setSubmission(
              EvaluationSubmission.fromServerEvaluationSubmission(
                respSubmission,
              ),
            ),
          )
          .catch((error) => navigate(-1));
      },
    );
  }, [
    submission,
    classroomId,
    submissionId,
    setSubmission,
    navigate,
    makeApiCall,
  ]);

  const handleExitEvaluation = () => {
    questions?.map((q) => CachingEngine.deleteData(`${q.question.id}-audio`));
    CachingEngine.deleteData('eval-question');
    setSubmission(undefined);
    navigate(-1);
  };

  const handleMissedWord = async (
    word: string,
    index: number,
    known: boolean,
    question?: Question,
  ) => {
    if (!submission || !question) return;

    const studentQuestionIdx = questions.findIndex(
      (sq) => sq.question.id === question?.id,
    );

    if (studentQuestionIdx === -1) return;

    const updatedQuestion = StudentQuestion.fromServerStudentQuestion({
      question: questions[studentQuestionIdx].question,
      response: {
        ...questions[studentQuestionIdx].response,
        response:
          questions[studentQuestionIdx].response.response +
          `\nmissed word: ${word} at index ${index}`,
        missed_words: [
          ...(questions[studentQuestionIdx].response.missed_words ?? []),
          { word: word, index: index },
        ],
      },
    });
    setQuestions((prev) => [
      ...prev.slice(0, studentQuestionIdx),
      updatedQuestion,
      ...prev.slice(studentQuestionIdx + 1),
    ]);
  };

  useEffect(() => {
    // This is where we will get the first set of questions for the evaluation
    if (
      !submission ||
      submission?.date_completed ||
      isInitialized ||
      questions.length
    )
      return;
    CachingEngine.getData('eval-question').then((cachedQuestions) => {
      if (cachedQuestions) {
        setQuestions(
          cachedQuestions.map((q: any) =>
            StudentQuestion.fromServerStudentQuestion(q),
          ),
        );
        setIsInitialized(true);
        return;
      }
      // if nothing cached, then fetch the questions
      if (submission.type === 'wida') {
        makeApiCall(
          evaluationService.getWidaQuestions,
          submissionId,
          submission.wida_listening ?? submission.baseline,
          submission.wida_reading ?? submission.baseline,
          submission.wida_speaking ?? submission.baseline,
          submission.wida_writing ?? submission.baseline,
        ).then((questions) => {
          setQuestions(questions);
          setIsInitialized(true);
          CachingEngine.setData('eval-question', questions);
        });
      } else {
        makeApiCall(
          evaluationService.getLexileQuestions,
          submissionId,
          submission.lexile_reading,
        ).then((questions) => {
          setQuestions(questions);
          setIsInitialized(true);
          CachingEngine.setData('eval-question', questions);
        });
      }
      setIsInitialized(true);
    });
  }, [
    submission,
    submissionId,
    questions,
    makeApiCall,
    isInitialized,
    setIsInitialized,
  ]);

  const handleQuestionResponseChanged = (
    question: Question,
    response: QuestionResponse,
  ) => {
    if (
      !questions ||
      ((response.response === '' || !response.response) &&
        (!response.choice || response.choice === '') &&
        question.question_type !== QuestionType.READALOUD)
    )
      return false;

    const updatedQuestion = {
      question: question,
      response: response,
    };

    const studentQuestionIdx = questions.findIndex(
      (sq) => sq.question.id === question?.id,
    );

    if (studentQuestionIdx === -1) return false;

    let updatedQuestions = questions.slice();
    updatedQuestions[studentQuestionIdx] =
      StudentQuestion.fromServerStudentQuestion(updatedQuestion);

    if (updatedQuestions.every((q: StudentQuestion) => q.response.completed)) {
      handleEvaluationPartCompleted(updatedQuestions);
      return true;
    } else {
      setQuestions(updatedQuestions);
    }
    // Return the updated question object
    return false;
  };

  const handleEvaluationPartCompleted = (
    finishedQuestions: StudentQuestion[],
  ) => {
    console.debug('evaluation part completed!');
    if (!submission) return;
    // submit the responses for this section
    CachingEngine.deleteData('eval-question').finally(() =>
      makeApiCall(
        evaluationService.submitEvaluationRound,
        submissionId,
        finishedQuestions,
        submission.round >= MAX_ROUNDS,
      )
        .then((updatedSubmission) => {
          setSubmission(
            EvaluationSubmission.fromServerEvaluationSubmission(
              updatedSubmission,
            ),
          );
          finishedQuestions.map((q) =>
            CachingEngine.deleteData(`${q.question.id}-audio`),
          );
          if (submission.round >= MAX_ROUNDS) return;
          // get the next round of questions
          if (submission.type === 'wida') {
            makeApiCall(
              evaluationService.getWidaQuestions,
              submissionId,
              updatedSubmission.submission.wida_listening,
              updatedSubmission.submission.wida_reading,
              updatedSubmission.submission.wida_speaking,
              updatedSubmission.submission.wida_writing,
            ).then((respQuestions) => {
              setQuestions([...respQuestions]);
              CachingEngine.setData('eval-question', respQuestions);
            });
          } else {
            makeApiCall(
              evaluationService.getLexileQuestions,
              submissionId,
              updatedSubmission.submission.lexile_reading,
            ).then((respQuestions) => {
              setQuestions([...respQuestions]);
              CachingEngine.setData('eval-question', respQuestions);
            });
          }
        })
        .catch((error) => alert(error)),
    );
  };

  const handleEvaluationFinished = () => {
    CachingEngine.deleteData('eval-question');
    navigate(-1);
  };

  const handleSubmitQuestion = () => {};

  const handleViewSettings = () => setViewSettings(false);

  if (!submission || !questions) return <AppPage />;

  return (
    <AppPage>
      <div className="app-page-content">
        <div className="app-main-content">
          <FontAwesomeIcon
            className="x-modal-button"
            onClick={handleExitEvaluation}
            style={{ backgroundColor: 'var(--pure-black)', color: 'white' }}
            icon={faXmark}
          />
          <h1>Round {submission.round}</h1>
          <MultipartAssignment
            questions={questions}
            onUpdateResponse={handleQuestionResponseChanged}
            onUpdateEvaluationResponse={handleQuestionResponseChanged}
            onMissedWord={handleMissedWord}
            onSubmit={handleSubmitQuestion}
            isLastRound={submission.round >= MAX_ROUNDS}
            teacherView={false}
            showFeedback={false}
            allowMultipleAttempts={false}
            isEvaluation={true}
            setActiveQuestion={() => {}}
          />
          <Modal isOpen={viewSettings} onClose={handleViewSettings}>
            <AssignmentSettings />
          </Modal>
          <EvaluationFinishedPopup
            isOpen={!!submission.date_completed}
            onClose={handleEvaluationFinished}
          />
        </div>
      </div>
    </AppPage>
  );
};

export default StudentEvaluationPage;
